/* eslint-disable react-hooks/exhaustive-deps,import/no-unresolved */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import moment from 'moment/moment';
import { isBrowser, isIOS, isMobile } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import { CustomDropdown } from 'assets/components';
import { byteUnitsOptions, TIER_LEVEL } from '../../../../../utilities/constants';
import { UpgradeAccountContext } from '../../../../../contexts/settings/upgradeAccount/context';
import { USER_DETAILS_UPDATE_KEY } from '../../../../../utilities/sharedMutations';
import { USER_CHANGE_TIER_LEVEL } from '../../../mutations';
import { showAlert } from '../../../../../utilities/notifications';
import { validateFileSize, validateMultiFileSizes } from '../../../../../utilities/formValidation';
import { handleFormBlur } from '../../../../../utilities/formsUtils';
import {
  dynamicAssets,
  getReadableFileSizeString,
  getWordCount,
} from '../../../../../utilities/helpers';
import ModalBodyLayout from '../partials/MainBodyLayout';
import { allowedExtension, MAX_FILES_NUMBER } from '../constant';
import RadioItem from '../../../../../components/RadioItem';
import Input from '../../../../../components/Input';
import { useUserPersonalInfo } from '../../../../../utilities/sharedHooks/user';

const NextLevelRequest = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const { isUS } = useUserPersonalInfo();

  const { state: locationState } = useLocation();

  const {
    state,
    openVerificationNotes,
    updateVerificationNotesType,
    updateSelectedSourceOfWealth,
    updateSelectedBusinessSector,
  } = useContext(UpgradeAccountContext);

  const { tiersStandard, currentTier, riskTier, selectedBusinessSector, selectedSourceOfWealth } =
    state;

  const hiddenFileInput = useRef(null);
  const hiddenSourceOfFundsFileInput = useRef(null);

  const [isSourceOfWealthOpened, setIsSourceOfWealthOpened] = useState(false);
  const [isOriginOfFundsOpened, setIsOriginOfFundsOpened] = useState(false);
  const [isBusinessSectorOpened, setIsBusinessSectorOpened] = useState(false);
  const [form, updateForm] = useState({
    sourceOfWealth: {
      key: selectedSourceOfWealth?.key || '',
      description: selectedSourceOfWealth?.description || '',
    },
    sourceOfWealthOther: '',
    businessSector: {
      key: selectedBusinessSector?.key || '',
      description: selectedBusinessSector?.description || '',
    },
    originOfFunds: { key: '', description: '' },
    proofOfAddress: null,
    sourceOfFunds: null,
    isPoliticallyExposed: { key: '', details: '' },
    hasCriminalRecord: { key: '', details: '' },
    selfEmploymentConfirmation: false,
    declarationAccepted: false,
  });

  const allowedFileSize = 2;
  const allowedFileSizeUnit = byteUnitsOptions.MB;

  const sourceOfWealthOptions = useMemo(() => {
    if (tiersStandard) {
      return tiersStandard?.filter((item) => item.risk_type_key === 'source_of_wealth');
    }
    return [];
  }, [tiersStandard]);

  useEffect(() => {
    if (!form?.sourceOfWealth?.key && selectedSourceOfWealth) {
      updateForm({ ...form, sourceOfWealth: selectedSourceOfWealth });
    }
    if (!form?.businessSector?.key && selectedBusinessSector) {
      updateForm({ ...form, businessSector: selectedBusinessSector });
    }
  }, [form, selectedSourceOfWealth, selectedBusinessSector]);

  useEffect(() => {
    if (form?.sourceOfWealth?.key) {
      updateSelectedSourceOfWealth(form?.sourceOfWealth);
    }
    if (form?.businessSector?.key) {
      updateSelectedBusinessSector(form?.businessSector);
    }
  }, [form]);

  const businessSectorOptions = useMemo(() => {
    if (tiersStandard) {
      return tiersStandard?.filter((item) => item.risk_type_key === 'business_sector');
    }
    return [];
  }, [tiersStandard]);

  const originOfFundsOptions = useMemo(() => {
    if (tiersStandard) {
      return tiersStandard?.filter((item) => item.risk_type_key === 'origin_of_funds');
    }
    return [];
  }, [tiersStandard]);

  const isUpgradeFromTwo = currentTier === TIER_LEVEL.TWO;

  const isUpgradeFromFour = currentTier === TIER_LEVEL.FOUR;

  const additionalQuestions = useMemo(
    () => [
      {
        key: 0,
        type: 'radio',
        label: translate('upgradeAccountPEPQuestion'),
        options: [
          { key: 'yes', label: translate('yes_label') },
          { key: 'no', label: translate('no_label') },
        ],
        furtherInfo: true,
        noteType: isUS ? 'pep' : '',
        name: 'isPoliticallyExposed',
      },
      {
        key: 1,
        type: 'radio',
        label: translate('upgradeAccountIllegalActivitiesQuestion'),
        options: [
          { key: 'yes', label: translate('yes_label') },
          { key: 'no', label: translate('no_label') },
        ],
        furtherInfo: true,
        name: 'hasCriminalRecord',
      },
      {
        key: 2,
        type: 'checkbox',
        label: translate('upgradeAccountSelfEmployedConfirmLabel'),
        hidden:
          (isUpgradeFromTwo && riskTier?.source_of_wealth !== 'employment_self_employed') ||
          (!isUpgradeFromTwo && form?.sourceOfWealth?.key !== 'employment_self_employed'),
        name: 'selfEmploymentConfirmation',
      },
      {
        key: 3,
        type: 'checkbox',
        label: translate('upgradeAccountConfirmLabel'),
        name: 'declarationAccepted',
      },
    ],
    [translate, form?.sourceOfWealth, riskTier, isUpgradeFromTwo, isUS]
  );

  const { mutateAsync: refetchUser } = useMutation(USER_DETAILS_UPDATE_KEY);

  const { mutate: changeTierLevel, isLoading: isLoadingChangeTierLevel } = useMutation(
    USER_CHANGE_TIER_LEVEL,
    {
      onSettled: async (_, error) => {
        if (!error) {
          await refetchUser();
          showAlert({
            message:
              isUpgradeFromTwo || isUpgradeFromFour
                ? translate('request_change_tier4_level_success_msg')
                : translate('request_change_tier_level_success_msg'),
            type: 'success',
          });
          history.replace('account');
        } else {
          showAlert({
            message: error?.message || translate('generic_error_msg'),
            type: 'danger',
          });
        }
      },
    }
  );

  const handleSubmit = () => {
    // TODO: add source of wealth other value to the payload

    if (isUpgradeFromTwo || isUpgradeFromFour) {
      const formData = new FormData();
      formData.append('origin_of_funds', form?.originOfFunds?.key);
      form?.proofOfAddress?.forEach((file) => {
        formData.append('proof_of_address', file);
      });
      form?.sourceOfFunds?.forEach((file) => {
        formData.append('source_of_funds', file);
      });
      formData.append(
        'is_politically_exposed',
        form?.isPoliticallyExposed?.key === 'yes' ? 'true' : 'false'
      );
      formData.append('pep_details', form?.isPoliticallyExposed?.details);
      formData.append(
        'has_criminal_record',
        form?.hasCriminalRecord?.key === 'yes' ? 'true' : 'false'
      );
      formData.append('criminal_record_details', form?.hasCriminalRecord?.details);
      if (
        (isUpgradeFromTwo && riskTier?.source_of_wealth === 'employment_self_employed') ||
        (!isUpgradeFromTwo && form?.sourceOfWealth?.key === 'employment_self_employed')
      ) {
        formData.append(
          'self_employment_confirmation',
          form?.selfEmploymentConfirmation ? 'true' : 'false'
        );
      }
      formData.append('declaration_accepted', form?.declarationAccepted ? 'true' : 'false');
      if (isUpgradeFromFour) {
        formData.append('source_of_wealth', form?.sourceOfWealth?.key);
        formData.append('business_sector', form?.businessSector?.key);
      }
      changeTierLevel(formData);
    } else {
      changeTierLevel({
        source_of_wealth: form?.sourceOfWealth?.key,
        business_sector: form?.businessSector?.key,
      });
    }
  };

  const isAllowedFileType = (file) => {
    if (file && file?.name) {
      const extension = file?.name.split('.').pop().toLowerCase();
      return allowedExtension.indexOf(extension) >= 0;
    }
    return true;
  };

  const isAllowedFilesType = (key) => {
    if (form[key] && form[key].length > 0) {
      const notAllowedFile = form[key].find((file) => {
        const extension = file?.name.split('.').pop().toLowerCase();
        return !allowedExtension.includes(extension);
      });
      if (notAllowedFile) {
        return false;
      }
      return true;
    }
    return true;
  };

  const disabledButton = useMemo(
    () =>
      (isUpgradeFromTwo &&
        (!form?.originOfFunds?.key ||
          !form?.sourceOfFunds ||
          !form?.proofOfAddress ||
          !form?.isPoliticallyExposed?.key ||
          !form?.hasCriminalRecord?.key ||
          (((isUpgradeFromTwo && riskTier?.source_of_wealth === 'employment_self_employed') ||
            (!isUpgradeFromTwo && form?.sourceOfWealth?.key === 'employment_self_employed')) &&
            !form?.selfEmploymentConfirmation) ||
          form?.sourceOfFunds?.length === 0 ||
          form?.proofOfAddress?.length === 0 ||
          !form?.declarationAccepted ||
          !validateMultiFileSizes(form?.sourceOfFunds, allowedFileSize, allowedFileSizeUnit) ||
          !validateMultiFileSizes(form?.proofOfAddress, allowedFileSize, allowedFileSizeUnit) ||
          !isAllowedFilesType('sourceOfFunds') ||
          !isAllowedFilesType('proofOfAddress'))) ||
      (isUpgradeFromFour &&
        (!form?.originOfFunds?.key ||
          !form?.sourceOfWealth?.key ||
          (form.sourceOfWealth?.key === 'other_specify_source_of_wealth' &&
            !form.sourceOfWealthOther) ||
          !form?.businessSector?.key ||
          !form?.isPoliticallyExposed?.key ||
          !form?.hasCriminalRecord?.key ||
          !form?.declarationAccepted ||
          (((isUpgradeFromTwo && riskTier?.source_of_wealth === 'employment_self_employed') ||
            (!isUpgradeFromTwo && form?.sourceOfWealth?.key === 'employment_self_employed')) &&
            !form?.selfEmploymentConfirmation) ||
          !form?.sourceOfFunds ||
          !form?.proofOfAddress ||
          form?.sourceOfFunds?.length === 0 ||
          form?.proofOfAddress?.length === 0 ||
          !validateMultiFileSizes(form?.sourceOfFunds, allowedFileSize, allowedFileSizeUnit) ||
          !validateMultiFileSizes(form?.proofOfAddress, allowedFileSize, allowedFileSizeUnit) ||
          !isAllowedFilesType('sourceOfFunds') ||
          !isAllowedFilesType('proofOfAddress'))) ||
      (!isUpgradeFromTwo &&
        !isUpgradeFromFour &&
        (!form?.sourceOfWealth?.key || !form?.businessSector?.key)) ||
      isLoadingChangeTierLevel,
    [form, allowedFileSize, allowedFileSizeUnit, isLoadingChangeTierLevel]
  );

  const renderFileImageUrl = (file) => {
    if (file && file?.name) {
      const extension = file?.name.split('.').pop().toLowerCase();
      switch (extension) {
        case 'png':
          return dynamicAssets('./assets/icons/png.svg');
        case 'pdf':
          return dynamicAssets('./assets/icons/pdf.svg');
        case 'doc':
        case 'docx':
          return dynamicAssets('./assets/icons/doc.svg');
        case 'jpg':
        case 'jpeg':
          return dynamicAssets('./assets/icons/jpg.svg');
        default:
          return dynamicAssets('./assets/icons/other.svg');
      }
    }
    return dynamicAssets('./assets/icons/other.svg');
  };

  const renderFooter = () => (
    <button
      className="btn btn-primary py-3 w-100"
      disabled={disabledButton}
      loading={isLoadingChangeTierLevel ? 'true' : 'false'}
      type="button"
      onClick={handleSubmit}>
      {translate('submit_request_btn_label')}
    </button>
  );

  const handleOnChange = (e, fieldName) => {
    if (!e) return;
    if (e?.target) {
      const { name, value, files } = e.target;
      const valueWordsNumber = value?.length > 0 ? getWordCount(value) : 0;
      if (name === 'sourceOfWealthOther' && valueWordsNumber > 31) {
        return;
      }
      if (form[name] && form[name].length > 0) {
        updateForm({ ...form, [name]: [...form[name], files[0]] });
      } else {
        updateForm({ ...form, [name]: [files[0]] });
      }
      hiddenSourceOfFundsFileInput.current.value = '';
      hiddenFileInput.current.value = '';
    } else {
      updateForm({ ...form, [fieldName]: e });
    }
  };

  const handleBlur = (e) => {
    handleFormBlur(e);
    if (!e || !e.target) return;
    e.target.closest('.form-group').className = e.target
      .closest('.form-group')
      .className.replace('has-focus', '');
  };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
    if (e.target.closest('.form-group').className.indexOf('has-focus') === -1) {
      e.target.closest('.form-group').className = `${
        e.target.closest('.form-group').className
      } has-focus`;
    }
  };

  const removeFile = (key, index) => {
    if (form[key] && form[key].length > 0) {
      const newFilesArr = form[key];
      newFilesArr.splice(index, 1);
      updateForm({ ...form, [key]: newFilesArr });
    }
  };

  const openDropdown = (type) => {
    if (type === 'sourceOfWealth') {
      setIsSourceOfWealthOpened((prev) => !prev);
      setIsBusinessSectorOpened(false);
      setIsOriginOfFundsOpened(false);
    }
    if (type === 'businessSector') {
      setIsBusinessSectorOpened((prev) => !prev);
      setIsSourceOfWealthOpened(false);
      setIsOriginOfFundsOpened(false);
    }
    if (type === 'originOfFunds') {
      setIsOriginOfFundsOpened((prev) => !prev);
      setIsSourceOfWealthOpened(false);
      setIsBusinessSectorOpened(false);
    }
  };

  const renderAdditionalQuestions = () =>
    additionalQuestions
      .filter((question) => !question.hidden)
      .map((question, index) => {
        switch (question.type) {
          case 'radio': {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`radio-${question.name}-${question.key}-${index}`}>
                <span className="text-f5 text-dark mt-4">
                  {question.label}
                  {question?.noteType && (
                    <i
                      className="icon icon-question-other text-shadow cursor-pointer text-hover ms-2"
                      style={{ fontSize: isBrowser ? '1.1rem' : '1.7rem', marginTop: '2px' }}
                      onClick={() => {
                        updateVerificationNotesType(question?.noteType);
                        openVerificationNotes();
                      }}
                      onKeyDown={() => {
                        updateVerificationNotesType(question?.noteType);
                        openVerificationNotes();
                      }}
                      role="presentation"
                    />
                  )}
                </span>

                {question.options.map((option) => (
                  <div key={option.key}>
                    <RadioItem
                      className="mt-2"
                      key={option.key}
                      label={option.label}
                      name={question.name}
                      onChange={() => {
                        updateForm({
                          ...form,
                          [question.name]: {
                            key: option.key,
                            details: form[question.name].details,
                          },
                        });
                      }}
                      checked={form[question.name].key === option.key}
                    />
                  </div>
                ))}
                {question.furtherInfo && (
                  <div>
                    <p className="text-f5 text-dark mt-5">
                      {translate('upgradeAccountMoreInfoLabel')}
                    </p>
                    <Input
                      type="text"
                      placeholder="Your answer"
                      groupClassName="mt-4"
                      value={form[question.name].details}
                      onChange={(e) => {
                        updateForm({
                          ...form,
                          [question.name]: {
                            key: form[question.name].key,
                            details: e?.target?.value,
                          },
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            );
          }
          case 'checkbox': {
            return (
              <div
                className="d-flex flex-row align-items-center"
                style={{ marginTop: index === additionalQuestions.length - 1 ? '2rem' : '' }}>
                <div>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name={question.name}
                    checked={form[question.name]}
                    onChange={() => {
                      updateForm({ ...form, [question.name]: !form[question.name] });
                    }}
                  />
                </div>
                <label className="form-check-label letter-spacing-sm">
                  <p className="ps-3 text-f5">{question.label}</p>
                </label>
              </div>
            );
          }
          default:
            return <></>;
        }
      });

  const renderRequiredFieldsFromTwoToThree = () => (
    <>
      <div className="form-group mt-4 mb-5" style={{ marginTop: 0 }}>
        <CustomDropdown
          name="originOfFunds"
          placeholder={translate('origin_of_funds_label')}
          onChange={(e) => handleOnChange(e, 'originOfFunds')}
          handleBlur={handleBlur}
          clearError={clearError}
          value={form.originOfFunds}
          options={originOfFundsOptions}
          isOpened={isOriginOfFundsOpened}
          setIsOpened={() => openDropdown('originOfFunds')}
        />
      </div>
      <div className="d-flex flex-column mb-4">
        <div
          className={`d-flex justify-content-between align-items-center ${
            form?.sourceOfFunds && form?.sourceOfFunds.length > 0 && 'mb-3'
          }`}>
          <p>{translate('source_of_funds_input_label')}</p>
          <div
            className="cursor-pointer text-hover"
            onClick={() => {
              updateVerificationNotesType('source_of_funds');
              openVerificationNotes();
            }}
            onKeyDown={() => {
              updateVerificationNotesType('source_of_funds');
              openVerificationNotes();
            }}
            role="presentation">
            <i
              className="icon icon-question-other text-shadow"
              style={{ fontSize: isBrowser ? '1.2rem' : '1.7rem' }}
            />
          </div>
        </div>
        {form?.sourceOfFunds &&
          form?.sourceOfFunds?.length > 0 &&
          form?.sourceOfFunds?.map((file, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="d-flex flex-column gap-2 mb-3" key={`${index}-${file?.name}`}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <div className="d-flex flex-row justify-content-start align-items-center">
                  <img src={renderFileImageUrl(file)} alt="File Type" />
                  <div className="d-flex flex-column ms-2">
                    <p className="text-f5 text-dark w-80 text-break">{file?.name}</p>
                    <p className="text-shadow test-f6">
                      {`${moment(file?.lastModifiedDate).format('DD MMM, YYYY [at] hh:mm')} `}{' '}
                      &#x2022; {` ${getReadableFileSizeString(file?.size).display}`}
                    </p>
                  </div>
                </div>
                <div
                  role="button"
                  tabIndex="-1"
                  onClick={() => removeFile('sourceOfFunds', index)}
                  onKeyDown={() => removeFile('sourceOfFunds', index)}>
                  <img src="./assets/icons/trash.svg" alt="Trash" />
                </div>
              </div>
              {!validateFileSize(file?.size, allowedFileSize, allowedFileSizeUnit) && (
                <label className="error-label text-danger">
                  {translate('input_file_size_error_label').replace(
                    '__SIZE__',
                    `${allowedFileSize} ${allowedFileSizeUnit}`
                  )}
                </label>
              )}
              {!isAllowedFileType(file) && (
                <label className="error-label text-danger">
                  {translate('input_file_type_error_label')}
                </label>
              )}
            </div>
          ))}

        {form.sourceOfFunds && form.sourceOfFunds.length >= 5 ? (
          <label className="error-label text-danger">
            {`You can only upload a maximum of ${MAX_FILES_NUMBER} files.`}
          </label>
        ) : (
          <>
            <div
              role="button"
              tabIndex="-1"
              onKeyDown={() => hiddenSourceOfFundsFileInput?.current.click()}
              onClick={() => hiddenSourceOfFundsFileInput?.current.click()}
              className="mt-3 d-flex flex-row justify-content-start align-items-center text-primary cursor-pointer">
              <i className="icon icon-add-outline me-2" />
              <p className="text-f5">{translate('upload_document_btn_label')}</p>
            </div>
            <input
              type="file"
              accept="image/*,.pdf"
              name="sourceOfFunds"
              ref={hiddenSourceOfFundsFileInput}
              onChange={handleOnChange}
              style={{ display: 'none' }}
            />
          </>
        )}
      </div>
      <div className="d-flex flex-column my-4">
        <div
          className={`d-flex justify-content-between align-items-center ${
            form?.proofOfAddress && form?.proofOfAddress.length > 0 && 'mb-3'
          }`}>
          <p>{translate('proof_of_address_input_label')}</p>
          <div
            className="cursor-pointer text-hover"
            onClick={() => {
              updateVerificationNotesType('proof_of_address');
              openVerificationNotes();
            }}
            onKeyDown={() => {
              updateVerificationNotesType('proof_of_address');
              openVerificationNotes();
            }}
            role="presentation">
            <i
              className="icon icon-question-other text-shadow"
              style={{ fontSize: isBrowser ? '1.2rem' : '1.7rem' }}
            />
          </div>
        </div>
        {form?.proofOfAddress &&
          form?.proofOfAddress?.length > 0 &&
          form?.proofOfAddress?.map((file, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="d-flex flex-column gap-2 mb-3" key={`${index}-${file?.name}`}>
              <div className="d-flex flex-row justify-content-between align-items-center">
                <div className="d-flex flex-row justify-content-start align-items-center">
                  <img src={renderFileImageUrl(file)} alt="File Type" />
                  <div className="d-flex flex-column ms-2">
                    <p className="text-f5 text-dark w-80 text-break">{file?.name}</p>
                    <p className="text-shadow test-f6">
                      {`${moment(file?.lastModifiedDate).format('DD MMM, YYYY [at] hh:mm')} `}{' '}
                      &#x2022; {` ${getReadableFileSizeString(file?.size).display}`}
                    </p>
                  </div>
                </div>
                <div
                  role="button"
                  tabIndex="-1"
                  onClick={() => removeFile('proofOfAddress', index)}
                  onKeyDown={() => removeFile('proofOfAddress', index)}>
                  <img src="./assets/icons/trash.svg" alt="Trash" />
                </div>
              </div>
              {!validateFileSize(file?.size, allowedFileSize, allowedFileSizeUnit) && (
                <label className="error-label text-danger">
                  {translate('input_file_size_error_label').replace(
                    '__SIZE__',
                    `${allowedFileSize} ${allowedFileSizeUnit}`
                  )}
                </label>
              )}
              {!isAllowedFileType(file) && (
                <label className="error-label text-danger">
                  {translate('input_file_type_error_label')}
                </label>
              )}
            </div>
          ))}
        {form.proofOfAddress && form.proofOfAddress.length >= 5 ? (
          <label className="error-label text-danger">
            {`You can only upload a maximum of ${MAX_FILES_NUMBER} files.`}
          </label>
        ) : (
          <>
            <div
              role="button"
              tabIndex="-1"
              onKeyDown={() => hiddenFileInput?.current.click()}
              onClick={() => hiddenFileInput?.current.click()}
              className="mt-3 d-flex flex-row justify-content-start align-items-center text-primary cursor-pointer">
              <i className="icon icon-add-outline me-2" />
              <p className="text-f5">{translate('upload_document_btn_label')}</p>
            </div>
            <input
              type="file"
              accept="image/*,.pdf"
              name="proofOfAddress"
              ref={hiddenFileInput}
              onChange={handleOnChange}
              style={{ display: 'none' }}
            />
          </>
        )}
      </div>
      {renderAdditionalQuestions()}
    </>
  );

  const renderRegularFields = () => (
    <>
      <div className="form-group mt-4 mb-4" style={{ marginTop: 0 }}>
        <CustomDropdown
          name="sourceOfWealth"
          placeholder={translate('source_of_wealth_label')}
          onChange={(e) => handleOnChange(e, 'sourceOfWealth')}
          handleBlur={handleBlur}
          clearError={clearError}
          value={form.sourceOfWealth}
          options={sourceOfWealthOptions}
          isOpened={isSourceOfWealthOpened}
          setIsOpened={() => openDropdown('sourceOfWealth')}
        />
      </div>
      {form.sourceOfWealth?.key === 'other_specify_source_of_wealth' && (
        <Input
          containerClass="w-100"
          groupClassName={`m-0 ${isIOS ? 'ios' : ''}`}
          placeholder={translate('other_source_of_wealth_label')}
          label={translate('other_source_of_wealth_label')}
          errorLabel={translate('input_required_error_label')}
          className={`${isIOS ? 'ios' : ''}`}
          name="sourceOfWealthOther"
          value={form.sourceOfWealthOther}
          onChange={handleOnChange}
        />
      )}
      <div className="form-group mt-3 mb-4" style={{ marginTop: 0 }}>
        <CustomDropdown
          name="businessSector"
          placeholder={translate('business_sector_label')}
          onChange={(e) => handleOnChange(e, 'businessSector')}
          handleBlur={handleBlur}
          clearError={clearError}
          value={form.businessSector}
          options={businessSectorOptions}
          isOpened={isBusinessSectorOpened}
          setIsOpened={() => openDropdown('businessSector')}
        />
      </div>
    </>
  );

  const renderRequiredFieldsFromFourToSix = () => (
    <>
      {renderRegularFields()}
      {renderRequiredFieldsFromTwoToThree()}
    </>
  );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() =>
        locationState?.openedFromCardOrder ? history.goBack() : history.replace('account')
      }
      title={translate('upgrade_account_modal_title')}>
      <p className="text-dark text-f5">{translate('upgrade_account_modal_subtitle')}</p>
      <p className="text-shadow text-f6 mt-2">{translate('upgrade_account_modal_note')}</p>
      {!isUpgradeFromTwo && !isUpgradeFromFour && renderRegularFields()}
      {isUpgradeFromTwo && renderRequiredFieldsFromTwoToThree()}
      {isUpgradeFromFour && renderRequiredFieldsFromFourToSix()}
      <div
        className={`d-flex flex-row mt-5 pt-3 ${
          isMobile ? 'position-fixed w-100 bg-light pb-3 px-2' : 'mx-2 px-5'
        }`}
        style={{ bottom: '0%', left: '0%', zIndex: 100 }}>
        {renderFooter()}
      </div>
    </ModalBodyLayout>
  );
};

export default NextLevelRequest;
